import * as Yup from "yup";
const noSpecialCharRegex = /^[A-Za-z0-9 ]*$/;
const digitsOnly = /^\d*[\.{1}\d*]\d*$/;
const notAllowCharacters = /[eE+-]/;
const alphaNumericWithCommaRegex = /^[A-Za-z0-9, ]*$/;
const notAllowSequence =
  /^(?!.*(\d)\1\1)(?!.*0123|1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321|3210)\d{4,6}$/;
const recurringNumberRegex = /\b(\d)\1+\b/;
const numberOnlyTempPassword = /^[0-9]+$/;

const numberOnlyCheck = (value) => {
  return value == null || !notAllowCharacters.test(value.toString());
};

const numberOnlyCheckTempPassword = (value: string) =>
  value === undefined || numberOnlyTempPassword.test(value.toString());

const notSequentialCheck = (value: string) =>
  value === undefined || notAllowSequence.test(value.toString());

const notRepetitiveCheck = (value: string) =>
  value === undefined || !recurringNumberRegex.test(value.toString());

const noWhitespace = () => {
  return Yup.string().test({
    name: "noWhitespace",
    message: "Whitespaces are not allowed",
    test: (value) => !/\s/.test(value),
  });
};

Yup.addMethod(Yup.string, "noWhitespace", noWhitespace);

const urlValidation = (value: string | null | undefined): boolean => {
  if (!value || value.trim() === "") return true;
  try {
    const url = new URL(value);
    // Accept http, https, ws, wss protocols
    return ["http:", "https:", "ws:", "wss:"].includes(url.protocol);
  } catch {
    return false;
  }
};

const additionalData = Yup.object().shape({
  Tag: Yup.string().required("Tag is required"),
  Value: Yup.string().required("Value is required"),
});

const tippingOptionValidate = Yup.number()
  .nullable()
  .notRequired()
  .min(0)
  .moreThan(-1, "Negative values not accepted")
  .when("TippingMode", {
    is: "tip_by_percentage",
    then: () =>
      Yup.number()
        .max(100, "Maximum value is 100%")
        .test("numberOnly", "Please enter number value only", (value) =>
          numberOnlyCheck(value)
        ),
  })
  .when("TippingMode", {
    is: "tip_by_value",
    then: () =>
      Yup.number()
        .max(100, "Maximum value is $100")
        .test("numberOnly", "Please enter number value only", (value) =>
          numberOnlyCheck(value)
        ),
  });

const processor = Yup.object().shape({
  ProcessorName: Yup.string().required("Processor Name is required"),
  TID: Yup.string().required("Processor TID is required"),
  MID: Yup.string().required("Processor MID is required"),
  STAN: Yup.string()
    .required("Processor STAN is required")
    .matches(/^[0-9]{6}$/, "Processor STAN must be exactly 6 digits"),
  PPID: Yup.string()
    .matches(noSpecialCharRegex, "Processor PPID contains invalid characters")
    .nullable()
    .notRequired(),
  AdditionalData: Yup.array().of(additionalData),
  ProcessorAdditionalDataKey1: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 1 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue1: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 1 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey2: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 2 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue2: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 2 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey3: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 3 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue3: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 3 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey4: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 4 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue4: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 4 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey5: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 5 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue5: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 5 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey6: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 6 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue6: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 6 contains invalid characters").nullable(),
  ProcessorAdditionalDataKey7: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Key 7 contains invalid characters").nullable(),
  ProcessorAdditionalDataValue7: Yup.string().matches(noSpecialCharRegex, "Processor Additional Data Value 7 contains invalid characters").nullable()
});

const application = Yup.object().shape({
  AppName: Yup.string().required("Application Name is required"),
  VersionString: Yup.string().required("Application Version is required"),
});
const payAtTableValidate = Yup.object()
  .required()
  .shape({
    HostMode: Yup.string().required(),
    HostURL: Yup.string()
      .when("HostMode", {
        is: "OPI",
        then: () => Yup.string().required("Pay at Table Host URL is required"),
      })
      .when("HostMode", {
        is: "HL",
        then: () => Yup.string().required("Pay at Table Host URL is required"),
      }),
    MerchantPassword: Yup.string(),
    StoreNumber: Yup.string()
      .when("HostMode", {
        is: "OPI",
        then: () => Yup.string().required("Pay at Table Store Number is required"),
      })
      .when("HostMode", {
        is: "MAITRED",
        then: () => Yup.string().required("Pay at Table Store Number is required"),
      })
      .when("HostMode", {
        is: "HL",
        then: () => Yup.string().required("Pay at Table Store Number is required"),
      }),
    UserNumber: Yup.string(),
    ClientId: Yup.string().when("HostMode", {
      is: "HL",
      then: () => Yup.string().required("Pay at Table Client ID is required"),
    }),
    ClientSecret: Yup.string().when("HostMode", {
      is: "HL",
      then: () => Yup.string().required("Pay at Table Client Secret is required"),
    }),
    TokenHostEndpoint: Yup.string(),
    TokenHostAudience: Yup.string(),
    PrintMerchantReceipt: Yup.boolean()
      .when("HostMode", {
        is: "OPI",
        then: () => Yup.boolean().required(),
      })
      .when("HostMode", {
        is: "MAITRED",
        then: () => Yup.boolean().required(),
      })
      .when("HostMode", {
        is: "HL",
        then: () => Yup.boolean().required(),
      }),
    EnableSplitPayment: Yup.boolean()
      .when("HostMode", {
        is: "OPI",
        then: () => Yup.boolean().required(),
      })
      .when("HostMode", {
        is: "MAITRED",
        then: () => Yup.boolean().required(),
      })
      .when("HostMode", {
        is: "HL",
        then: () => Yup.boolean().required(),
      }),
  });

const schema = Yup.object({
  attributes: Yup.object({
    TerminalType: Yup.string().required("Terminal Type cannot be blank"),
    SerNo: Yup.string().required("Serial Number cannot be blank"),
    Shard: Yup.number()
      .required("Shard cannot be blank")
      .min(1000, "Shard must be greater than or equal to 1000")
      .max(3000, "Shard must be less than or equal to 3000"),
    TID: Yup.string()
      .matches(noSpecialCharRegex, "TID contains invalid characters")
      .required("TID cannot be blank")
      .min(8, "TID must be 8 characters")
      .max(8, "TID must be 8 characters"),
    LowValueTransactionLimit: Yup.number()
      .moreThan(-1, "Low Value Transaction Limit: Negative values not accepted"),
    POIID: Yup.string()
      .matches(noSpecialCharRegex, "POIID contains invalid characters")
      .nullable()
      .notRequired(),
    Location: Yup.string().nullable().notRequired(),
    Address1: Yup.string()
      .matches(alphaNumericWithCommaRegex, "Address 1 contains invalid characters")
      .nullable()
      .notRequired(),
    Address2: Yup.string()
      .matches(alphaNumericWithCommaRegex, "Address 2 contains invalid characters")
      .nullable()
      .notRequired(),
    TZ: Yup.string().required("Timezone cannot be blank"),
    Processor: Yup.array().of(processor),
    Applications: Yup.array().of(application),
    FallbackRetryCount: Yup.number()
      .min(0)
      .moreThan(-1, "Fallback Retry Count: Negative values not accepted")
      .notRequired(),
    FeatureCashOutLimit: Yup.string(), //.matches(digitsOnly, 'Please ensure this is a currency format'),

    CloudPosTimeout: Yup.number()
      .min(0)
      .moreThan(-1, "Cloud POS Timeout: Negative values not accepted")
      .notRequired(),
    InactivityTimeout: Yup.number()
      .min(0)
      .moreThan(-1, "Inactivity Timeout: Negative values not accepted")
      .notRequired(),
    ResultTimeout: Yup.number()
      .min(0)
      .moreThan(-1, "Result Timeout: Negative values not accepted")
      .notRequired(),
    TerminalStatusReportingIntervalSeconds: Yup.number()
      .min(0)
      .moreThan(-1, "Terminal Status Reporting Interval: Negative values not accepted")
      .notRequired(),
    TerminalGPSUpdateIntervalSeconds: Yup.number()
      .min(0)
      .moreThan(-1, "Terminal GPS Update Interval: Negative values not accepted")
      .notRequired(),
    WindowForCDCRefundOptionSeconds: Yup.number()
      .min(0)
      .moreThan(-1, "Window for CDC Refund Option: Negative values not accepted")
      .notRequired(),
    TippingMode: Yup.string().nullable().notRequired(),
    TipWithPercentagePercentage1: tippingOptionValidate,
    TipWithPercentagePercentage2: tippingOptionValidate,
    TipWithPercentagePercentage3: tippingOptionValidate,
    SurchargeGST: Yup.boolean().optional(),
    SurchargePercentage: Yup.array().when("SurchargeGST", {
      is: true,
      then: () =>
        Yup.array(
          Yup.object()
            .nullable()
            .shape({
              Percentage: Yup.number()
                .nullable()
                .notRequired()
                .min(0)
                .moreThan(-1, "Surcharge Percentage: Negative values not accepted")
                .max(100, "Surcharge Percentage: Maximum value is 100%")
                .test("numberOnly", "Surcharge Percentage: Please enter number value only", (value) =>
                  numberOnlyCheck(value)
                ),
              SurchargeGST: Yup.number()
                .required("Surcharge GST is required")
                .min(0)
                .moreThan(-1, "Surcharge GST: Negative values not accepted")
                .max(100, "Surcharge GST: Maximum value is 100%")
                .test("numberOnly", "Surcharge GST: Please enter number value only", (value) =>
                  numberOnlyCheck(value)
                ),
            })
        ),
      otherwise: () =>
        Yup.array(
          Yup.object()
            .nullable()
            .shape({
              Percentage: Yup.number()
                .nullable()
                .notRequired()
                .min(0)
                .moreThan(-1, "Surcharge Percentage: Negative values not accepted")
                .max(100, "Surcharge Percentage: Maximum value is 100%")
                .test("numberOnly", "Surcharge Percentage: Please enter number value only", (value) =>
                  numberOnlyCheck(value)
                ),
              SurchargeGST: Yup.number()
                .min(0)
                .moreThan(-1, "Surcharge GST: Negative values not accepted")
                .max(100, "Surcharge GST: Maximum value is 100%")
                .test("numberOnly", "Surcharge GST: Please enter number value only", (value) =>
                  numberOnlyCheck(value)
                ),
            })
        ),
    }),
    PreAuth: Yup.object().shape({
      ExpiryDays: Yup.number().moreThan(0, "PreAuth Expiry Days can range from 1-28").lessThan(29, "PreAuth Expiry Days can range from 1-28"),
    }),
    Manufacturer: Yup.string(),
    SurchargeV2: Yup.array().when("Manufacturer", {
      is: (value: String) => value && value.toUpperCase() === "QUEST",
      then: () => Yup.array().notRequired(),
      otherwise: () =>
        Yup.array().of(
          Yup.object()
            .nullable()
            .shape({
              CardType: Yup.mixed().required("Surcharge V2 Card Type is required"),
              SurchargeType: Yup.string().required(
                "Surcharge V2 Surcharge Type is required"
              ),
              PurchaseSurchargeValue: Yup.number()
                .min(0)
                .moreThan(-1, "Surcharge V2 Purchase Surcharge Value: Negative values not accepted")
                .max(100, "Surcharge V2 Purchase Surcharge Value: Maximum value is 100")
                .test(
                  "numberOnly",
                  "Surcharge V2 Purchase Surcharge Value: Please enter a number value only",
                  (value) => numberOnlyCheck(value)
                ),
              CashoutSurchargeValue: Yup.number()
                .min(0)
                .moreThan(-1, "Surcharge V2 Cashout Surcharge Value: Negative values not accepted")
                .max(100, "Surcharge V2 Cashout Surcharge Value: Maximum value is 100")
                .test(
                  "numberOnly",
                  "Surcharge V2 Cashout Surcharge Value: Please enter a number value only",
                  (value) => numberOnlyCheck(value)
                ),
              GSTValue: Yup.number()
                .min(0)
                .moreThan(-1, "Surcharge V2 GST Value: Negative values not accepted")
                .max(100, "Surcharge V2 GST Value: Maximum value is 100%")
                .test(
                  "numberOnly",
                  "Surcharge V2 GST Value: Please enter a number value only",
                  (value) => numberOnlyCheck(value)
                ),
            })
        ),
    }),
    SurchargeDisclaimerText: Yup.string().when("SurchargeShowDisclaimer", {
      is: true,
      then: (schema: any) => {
        return schema.required("Surcharge Disclaimer Text is required");
      },
    }),

    FixedSurchargePercentage: Yup.number()
      .nullable()
      .notRequired()
      .min(0)
      .moreThan(-1, "Fixed Surcharge Percentage: Negative values not accepted")
      .max(100, "Fixed Surcharge Percentage: Maximum value is 100%")
      .test("numberOnly", "Fixed Surcharge Percentage: Should be a number value only", (value) =>
        numberOnlyCheck(value)
      ),
    FixedSurchargeAmount: Yup.number()
      .nullable()
      .notRequired()
      .min(0)
      .moreThan(-1, "Fixed Surcharge Amount: Negative values not accepted")
      .max(9999, "Fixed Surcharge Amount: Maximum value is 99.99")
      .test("numberOnly", "Fixed Surcharge Amount: Should be a number value only", (value) =>
        numberOnlyCheck(value)
      ),
    MCRMinAmount: Yup.number()
      .nullable()
      .positive()
      .notRequired()
      .min(0)
      .max(999999999, "MCR Min Amount: Maximum value is 9999999.99")
      .moreThan(-1, "MCR Min Amount: Negative values are not accepted"),
    PayAtTableEnabled: Yup.string(),
    PayAtTable: Yup.object()
      .nullable()
      .when("PayAtTableEnabled", {
        is: "Yes",
        then: () => {
          return payAtTableValidate;
        },
      }),
    DRCMinLoyaltyAmount: Yup.number()
      .when("FeatureDRCLoyaltyEnabled", {
        is: "Yes",
        then: (schema: any) => {
          return schema.required("DRC Min Loyalty Amount is required");
        },
      })
      .nullable()
      .test("numberOnly", "DRC Min Loyalty Amount: Should be a number value only", (value) =>
        numberOnlyCheck(value)
      )
      .integer("DRC Min Loyalty Amount: Must be an integer")
      // .required("MINIMUM LOYALTY AMOUNT is required")
      .positive()
      .min(0)
      .max(999999.99)
      .moreThan(-1, "DRC Min Loyalty Amount: Negative values not accepted"),
    DRCStoreId: Yup.string().when("FeatureDRCLoyaltyEnabled", {
      is: "Yes",
      then: (schema: any) => {
        return schema.noWhitespace().required("DRC Store ID is required");
      },
    }),
    DRCLoyaltyLogoUrl: Yup.string().when("FeatureDRCLoyaltyEnabled", {
      is: "Yes",
      then: (schema: any) => {
        return schema
          .noWhitespace()
          .required("DRC Loyalty Logo URL is required");
      },
    }),
    FeatureDRCLoyaltyCashPassword: Yup.string().when(
      "FeatureDRCLoyaltyEnabled",
      {
        is: "Yes",
        then: (schema: any) => {
          return schema.required("DRC Loyalty Cash Password is required");
        },
      }
    ),
    ScreenDimmingTimeoutMs: Yup.number()
      .nullable()
      .integer("Screen Dimming Timeout: Must be an integer")
      .positive()
      .notRequired()
      .min(0)
      .max(9999999, "Screen Dimming Timeout: Maximum value is 9,999,999")
      .moreThan(-1, "Screen Dimming Timeout: Negative values are not accepted"),
    MastercardMCRThreshold: Yup.number()
      .nullable()
      .positive()
      .notRequired()
      .min(0)
      .max(999999999, "Mastercard MCR Threshold: Maximum value is 9,999,999.99")
      .moreThan(-1, "Mastercard MCR Threshold: Negative values are not accepted"),
    VisaMCRThreshold: Yup.number()
      .nullable()
      .positive()
      .notRequired()
      .min(0)
      .max(999999999, "Visa MCR Threshold: Maximum value is 9,999,999.99")
      .moreThan(-1, "Visa MCR Threshold: Negative values are not accepted"),
    FeatureRefundLimit: Yup.number()
      .nullable()
      .integer("Feature Refund Limit: Must be an integer")
      .positive()
      .notRequired()
      .min(0)
      .max(99999999, "Feature Refund Limit: Maximum value is 999,999.99")
      .moreThan(-1, "Feature Refund Limit: Negative values are not accepted"),
    FeatureRefundDailyLimit: Yup.number()
      .nullable()
      .integer("Feature Refund Daily Limit: Must be an integer")
      .positive()
      .notRequired()
      .min(0)
      .max(99999999, "Feature Refund Daily Limit: Maximum value is 999,999.99")
      .moreThan(-1, "Feature Refund Daily Limit: Negative values are not accepted"),
    BatteryLimit: Yup.number()
      .nullable()
      .integer("Battery Limit: Must be an integer")
      .positive()
      .notRequired()
      .min(1)
      .max(99, "Battery Limit: Maximum value is 99")
      .moreThan(-1, "Battery Limit: Negative values are not accepted"),
    TransitBlockListRefreshIntervalMs: Yup.number()
      .notRequired()
      .integer("Transit Block List Refresh Interval: Decimal values not accepted")
      .min(10)
      .moreThan(9, "Transit Block List Refresh Interval: Minimum value must be 10 minutes")
      .test("numberOnly", "Transit Block List Refresh Interval: Please enter number value only", (value) =>
        numberOnlyCheck(value)
      ),
    TempPassword: Yup.string()
      .min(4, "Temporary Password: Must be between 4 and 6 digit numbers")
      .max(6, "Temporary Password: Must be between 4 and 6 digit numbers")
      .test("numberOnly", "Temporary Password: Cannot be alphanumeric values", (value) =>
        numberOnlyCheckTempPassword(value)
      )
      .test("notRepetitive", "Temporary Password: Cannot be repetitive numbers", (value) =>
        notRepetitiveCheck(value)
      )
      .test("notSequence", "Temporary Password: Cannot be sequential numbers", (value) =>
        notSequentialCheck(value)
      ),
      PinRetryAttemptCount: Yup.number()
      .notRequired()
      .integer("Pin Retry Attempt Count: Decimal values not accepted")
      .min(0)
      .max(3, "Pin Retry Attempt Count: Maximum value is 3")
      .moreThan(-1, "Pin Retry Attempt Count: Negative values are not accepted")
      .test("numberOnly", "Pin Retry Attempt Count: Please enter number value only", (value) =>
        numberOnlyCheck(value)
      ),
    URL: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "URL must be a valid URL", urlValidation),
    URLBackup: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "URL Backup must be a valid URL", urlValidation),
    URLUpgrade: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "URL Upgrade must be a valid URL", urlValidation),
    URLUpgradeBackup: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "URL Upgrade Backup must be a valid URL", urlValidation),
    URLUpgradeFirmware: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "TERMINAL FIRMWARE UPGRADE URL must be a valid URL", urlValidation),
    URLUpgradeFirmwareBackup: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "URL Upgrade Firmware Backup must be a valid URL", urlValidation),
    DockerFirmware: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "Base Firmware URL must be a valid URL", urlValidation),
    DockerFirmwareBackup: Yup.string()
      .nullable()
      .notRequired()
      .test("url", "Docker Firmware Backup URL must be a valid URL", urlValidation),
  }),
});

export default schema;
